import { useState, type DragEvent } from 'react';

import { PhotoEmptyFrame } from './PhotoEmptyFrame';
import { PhotoFilledFrame } from './PhotoFilledFrame';
import { PhotoReadingOverlay } from './PhotoReadingOverlay';
import { PhotoUploadOverlay } from './PhotoUploadOverlay';

export interface PhotoPreviewProps {
  /** The photo to show, or null for the empty frame. */
  src: string | null;
  venueName: string;
  /** True while a chosen photo is being read. */
  reading: boolean;
  /** 0-100 while an upload is in flight, otherwise null. */
  uploadProgress: number | null;
  /** Whether a file can be dropped onto the frame. */
  canDrop: boolean;
  onDropFile: (file: File) => void;
}

/** The booking photo in the 16:9 frame customers see, with a drop target for desktop. */
export function PhotoPreview({
  src,
  venueName,
  reading,
  uploadProgress,
  canDrop,
  onDropFile,
}: PhotoPreviewProps) {
  const [over, setOver] = useState(false);

  const dropProps = canDrop
    ? {
        onDragOver: (e: DragEvent) => {
          e.preventDefault();
          setOver(true);
        },
        onDragLeave: () => setOver(false),
        onDrop: (e: DragEvent) => {
          e.preventDefault();
          setOver(false);
          const file = e.dataTransfer.files[0];
          if (file) onDropFile(file);
        },
      }
    : {};

  return (
    <div
      {...dropProps}
      data-testid="photo-preview"
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: 10,
        overflow: 'hidden',
        flex: '0 0 auto',
        background: over ? 'var(--nb-primary-soft)' : 'var(--nb-surface)',
        border: src
          ? '1px solid var(--nb-border)'
          : `1.5px dashed ${over ? 'var(--nb-primary)' : 'var(--nb-border-2)'}`,
        outline: over ? '2px solid var(--nb-primary)' : undefined,
      }}
    >
      {src ? (
        <PhotoFilledFrame src={src} venueName={venueName} />
      ) : (
        <PhotoEmptyFrame canDrop={canDrop} />
      )}
      {reading && <PhotoReadingOverlay />}
      {uploadProgress !== null && <PhotoUploadOverlay progress={uploadProgress} />}
    </div>
  );
}
